import { isPrimitive } from './utils';
import { isObject } from './object';
import { isDate } from './date';

export function defaultWidgetDataAdapter(data: any = {}, modelData: any = {}): any {
    return adaptWidgetData(data, modelData, true);
}

/** The paths an edit changed: `true` where the value changed, a branch per key below it. */
export type EditedPaths = true | { [key: string]: EditedPaths };

export function stripUntouchedFills(current: any, stored: any, filled: any, edited?: EditedPaths): any {
    if (edited === true) {
        return current;
    }

    if (Array.isArray(current) && Array.isArray(stored)) {
        return current.map((item, index) =>
            index < stored.length
                ? stripUntouchedFills(
                      item,
                      stored[index],
                      Array.isArray(filled) ? filled[index] : undefined,
                      edited?.[index]
                  )
                : item
        );
    }

    if (!isObject(current) || !isObject(stored)) {
        return current;
    }

    const result: Record<string, any> = {};
    Object.keys(current).forEach((key: string) => {
        if (stored[key] !== undefined) {
            result[key] = stripUntouchedFills(
                current[key],
                stored[key],
                isObject(filled) ? filled[key] : undefined,
                edited?.[key]
            );
            return;
        }

        if (edited?.[key] || !isUntouchedFill(current[key], isObject(filled) ? filled[key] : undefined)) {
            result[key] = current[key];
        }
    });

    return result;
}

export function changedPaths(previous: any, next: any): EditedPaths | undefined {
    if (previous === next) {
        return undefined;
    }

    const bothArrays = Array.isArray(previous) && Array.isArray(next) && previous.length === next.length;
    if (!bothArrays && !(isObject(previous) && isObject(next))) {
        return true;
    }

    const changed: Record<string, EditedPaths> = {};
    new Set([...Object.keys(previous), ...Object.keys(next)]).forEach((key: string) => {
        const paths = changedPaths(previous[key], next[key]);
        if (paths) {
            changed[key] = paths;
        }
    });

    return Object.keys(changed).length ? changed : undefined;
}

export function mergeEditedPaths(a: EditedPaths | undefined, b: EditedPaths | undefined): EditedPaths | undefined {
    if (a === true || b === true) {
        return true;
    }

    if (!a || !b) {
        return a ?? b;
    }

    const merged: Record<string, EditedPaths> = { ...a };
    Object.keys(b).forEach((key: string) => {
        merged[key] = mergeEditedPaths(a[key], b[key]) as EditedPaths;
    });

    return merged;
}

// Every value `current` holds is the filled one. The fill may hold more: the store keeps the load adapter's copy
// until the widget's signal form emits, and the form's own backfill adds keys the adapter left out (`mobile*`).
function isUntouchedFill(current: any, filled: any): boolean {
    if (current === filled) {
        return true;
    }

    if (Array.isArray(current) || Array.isArray(filled)) {
        return (
            Array.isArray(current) &&
            Array.isArray(filled) &&
            current.length === filled.length &&
            current.every((item, index) => isUntouchedFill(item, filled[index]))
        );
    }

    if (!isObject(current) || !isObject(filled)) {
        return false;
    }

    return Object.keys(current).every(
        (key) => current[key] === undefined || isUntouchedFill(current[key], filled[key])
    );
}

function adaptWidgetData(data: any = {}, modelData: any = {}, isRoot: boolean): any {
    const adaptedData = { ...data };
    Object.keys(modelData).forEach((key: string) => {
        // A device override the widget never had stays absent: renderers read that as "inherit", while
        // the model default would pin it (an image centred on desktop went left on tablet, #69719 bug 19).
        // `tablet*` only at the root — the alignment and size switches, not the `position` grid.
        if (key.startsWith('mobile') || (isRoot && key.startsWith('tablet'))) {
            return;
        }

        // if no key in widget data and it is primitive, set default value from model
        if (adaptedData[key] === undefined && isPrimitive(modelData[key])) {
            adaptedData[key] = modelData[key];
            return;
        }

        if (Array.isArray(modelData[key])) {
            // if no key in widget data and it is array, spread default array value from model
            if (adaptedData[key] === undefined || !Array.isArray(adaptedData[key])) {
                adaptedData[key] = [...modelData[key]];
                return;
            }

            adaptedData[key] = adaptedData[key].map((item: any) => {
                // if item is primitive, return it
                if (isPrimitive(item)) {
                    return item;
                }

                // if item is object, recursively call this function
                return adaptWidgetData(item, modelData[key][0], false);
            });
            return;
        }

        if (isObject(modelData[key]) && !isDate(modelData[key])) {
            adaptedData[key] = adaptWidgetData(adaptedData[key], modelData[key], false);
        }
    });

    return adaptedData;
}
